import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Link, 
  Paper, 
  Grid, 
  Divider, 
  MenuItem, 
  Select, 
  FormControl, 
  InputLabel, 
  CircularProgress, 
  Snackbar, 
  Alert, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  Card,
  CardContent,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon, 
  Help as HelpIcon, 
  QuestionAnswer as QuestionAnswerIcon, 
  Email as EmailIcon, 
  Phone as PhoneIcon,
  Send as SendIcon,
  ContactSupport as ContactSupportIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Özel stil bileşenleri
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: 16,
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
  },
}));

const AnimatedButton = styled(Button)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  transition: 'background-color 0.3s ease-in-out',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(255, 255, 255, 0.2)',
    transform: 'translateX(-100%)',
  },
  '&:hover::after': {
    transform: 'translateX(0)',
    transition: 'transform 0.3s ease-in-out',
  },
}));

const AnimatedTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    transition: 'border-color 0.3s ease-in-out',
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const SupportCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  transition: 'transform 0.3s ease-in-out',
  cursor: 'pointer',
  height: '100%',
  '&:hover': {
    transform: 'scale(1.05)',
  },
}));

const CategoryChip = styled(Chip)(({ theme, isselected }) => ({
  margin: theme.spacing(0.5),
  transition: 'all 0.3s ease',
  backgroundColor: isselected === 'true' ? theme.palette.primary.main : theme.palette.background.default,
  color: isselected === 'true' ? theme.palette.primary.contrastText : theme.palette.text.primary,
  '&:hover': {
    backgroundColor: isselected === 'true' ? theme.palette.primary.dark : theme.palette.action.hover,
  },
}));

// Ana bileşen
const SupportPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    category: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [activeCategory, setActiveCategory] = useState('general');

  const categories = [
    { id: 'general', label: 'Genel Sorular' },
    { id: 'technical', label: 'Teknik Destek' },
   /*  { id: 'billing', label: 'Ödeme ve Fatura' }, */
    { id: 'feature', label: 'Özellik Talepleri' }
  ];

  const faqs = [
    {
      question: 'Formumu nasıl oluşturabilirim?',
      answer: 'Form oluşturmak için "Form Oluştur" menüsüne tıklayarak form oluşturma sayfasına gidebilirsiniz. Burada form başlığı, açıklaması ve alanlarını belirleyebilirsiniz.'
    },
    {
      question: 'Klasörleri nasıl kullanabilirim?',
      answer: 'Klasörler, formlarınızı organize etmenizi sağlar. "Klasör Oluştur" menüsünden yeni klasörler oluşturabilir ve formlarınızı bu klasörlere taşıyabilirsiniz.'
    },
    {
      question: 'Formu paylaşmak için ne yapmalıyım?',
      answer: 'Formunuzu paylaşmak için formun detay sayfasında bulunan "Paylaş" butonuna tıklayabilir ve paylaşım bağlantısını kopyalayabilirsiniz.'
    },
    {
      question: 'Form yanıtlarına nasıl ulaşabilirim?',
      answer: 'Form yanıtlarına, formun detay sayfasındaki "Yanıtlar" sekmesinden ulaşabilirsiniz. Burada tüm yanıtları görebilir ve dışa aktarabilirsiniz.'
    }
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Hata varsa temizle
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Ad alanı zorunludur';
    if (!formData.email.trim()) {
      newErrors.email = 'E-posta alanı zorunludur';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Geçerli bir e-posta adresi giriniz';
    }
    if (!formData.message.trim()) newErrors.message = 'Mesaj alanı zorunludur';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    // Simüle edilmiş API çağrısı
    try {
      // Gerçek API entegrasyonunda bu kısmı değiştirin
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      console.log('Destek talebi gönderildi:', formData);
      
      setSnackbar({
        open: true,
        message: 'Mesajınız başarıyla gönderildi. En kısa sürede size dönüş yapacağız.',
        severity: 'success'
      });
      
      // Formu temizle
      setFormData({
        name: '',
        email: '',
        subject: '',
        category: '',
        message: ''
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Bir hata oluştu. Lütfen daha sonra tekrar deneyin.',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const setCategory = (categoryId) => {
    setActiveCategory(categoryId);
    setFormData({ ...formData, category: categoryId });
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: isMobile ? 2 : 4 }}>
      <Box sx={{ mb: 5, textAlign: 'center' }}>
        <Typography variant="h3" component="h1" 
          sx={{ 
            fontWeight: 700, 
            mb: 2,
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
          Nasıl Yardımcı Olabiliriz?
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          Sorularınız veya destek talepleriniz için aşağıdaki formu doldurabilir veya sık sorulan sorulara göz atabilirsiniz.
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Sol taraf - Destek formları */}
        <Grid item xs={12} md={7}>
          <StyledPaper elevation={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <ContactSupportIcon sx={{ fontSize: 28, mr: 1, color: 'primary.main' }} />
              <Typography variant="h5" component="h2" fontWeight={600}>
                Destek Talebi Oluştur
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>Kategori seçin:</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                {categories.map(category => (
                  <CategoryChip
                    key={category.id}
                    label={category.label}
                    onClick={() => setCategory(category.id)}
                    isselected={activeCategory === category.id ? 'true' : 'false'}
                    icon={category.id === 'technical' ? <HelpIcon /> : undefined}
                  />
                ))}
              </Box>
            </Box>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <AnimatedTextField
                    fullWidth
                    label="Adınız"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    error={!!errors.name}
                    helperText={errors.name}
                    required
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: 2 }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <AnimatedTextField
                    fullWidth
                    label="E-posta"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!errors.email}
                    helperText={errors.email}
                    required
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: 2 }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <AnimatedTextField
                    fullWidth
                    label="Konu"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: 2 }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <AnimatedTextField
                    fullWidth
                    label="Mesajınız"
                    name="message"
                    multiline
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    error={!!errors.message}
                    helperText={errors.message}
                    required
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: 2 }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <AnimatedButton
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={isSubmitting}
                    endIcon={isSubmitting ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
                    sx={{ 
                      borderRadius: 2,
                      py: 1.5,
                      fontWeight: 600
                    }}
                  >
                    {isSubmitting ? 'Gönderiliyor...' : 'Talebi Gönder'}
                  </AnimatedButton>
                </Grid>
              </Grid>
            </form>
          </StyledPaper>
        </Grid>

        {/* Sağ taraf - FAQ ve iletişim bilgileri */}
        <Grid item xs={12} md={5}>
          <Box sx={{ mb: 4 }}>
            <StyledPaper elevation={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <QuestionAnswerIcon sx={{ fontSize: 28, mr: 1, color: 'primary.main' }} />
                <Typography variant="h5" component="h2" fontWeight={600}>
                  Sık Sorulan Sorular
                </Typography>
              </Box>
              
              {faqs.map((faq, index) => (
                <Accordion key={index} disableGutters sx={{ 
                  mb: 1, 
                  boxShadow: 'none', 
                  '&:before': { display: 'none' },
                  borderRadius: 2,
                  overflow: 'hidden'
                }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ 
                      backgroundColor: theme.palette.background.default,
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={500}>{faq.question}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary">
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
              
              <Box sx={{ mt: 2 }}>
                {/* <Link href="/faq" underline="hover" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  <Typography variant="body2" color="primary">
                    Tüm SSS
                  </Typography>
                  <ExpandMoreIcon sx={{ transform: 'rotate(-90deg)', fontSize: 16 }} />
                </Link> */}
              </Box>
            </StyledPaper>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={12}>
              <SupportCard>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <EmailIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" component="h3">
                      E-posta İle
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Sorularınız için e-posta adresimize yazabilirsiniz
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    destek@formbuilder.com
                  </Typography>
                </CardContent>
              </SupportCard>
            </Grid>
            <Grid item xs={12} sm={6} md={12}>
              <SupportCard>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PhoneIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" component="h3">
                      Telefonla
                    </Typography>
                  </Box>
              {/*     <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Hafta içi 09:00-18:00 saatlerinde telefonla ulaşabilirsiniz
                  </Typography> */}
                  <Typography variant="body2" fontWeight={500}>
                    +90 (549) 245 00 02
                  </Typography>
                </CardContent>
              </SupportCard>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SupportPage;